import { faChevronDown, faChevronUp, faRefresh, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { callQuery, GET_FILE_AT_VERSION, PatientFileVersion } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";
import { EXPLAIN_PATIENT_SUMMARY, SCAN_MULTI } from "../../../../amplify/appsync/write";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { isSectionUnread, processTextForCopy } from "../utils";
import CopyToClipboardButton from "../../../assistant/CopyToClipboardButton";
import { dashboardLLMCache } from "../cache";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../../utils/time";

type SectionPatientSummaryProps = {
    className?: string;
    isLoadingPatientSummary: any;
    patientSummary: any;
    patientVisit?: PatientVisit;
    feedbacks: any;
    setFeedbacks: any;
    statuses: any;
    setRegenerateTarget: any;
    setShowContextMenu: any;
    handleShowScanMultiModal: any;
    unreadFileVersionMap: any;
    latestFileVersions?: Record<string, PatientFileVersion>;
    sectionToSetter?: any;
    handleFileVersionChange?: any;
    user: any;
    tenant: any;
};

export const SectionPatientSummary = React.memo(({
    className = '',
    isLoadingPatientSummary,
    patientSummary,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    unreadFileVersionMap,
    user,
    tenant,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: SectionPatientSummaryProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_PATIENT_SUMMARY]) {
            const version = latestFileVersions[EXPLAIN_PATIENT_SUMMARY];

            // console.log("[Patient Summary] Loading dashboardLLMCache with version ", version.source_version_id);
            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                // console.log("[Patient Summary] Found citation_mapping", data.citation_mapping);
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    if (!isLoadingPatientSummary && (!patientSummary || patientSummary["error"])) {
        return (
            <>
                <GenerateModuleHeroV2 type={EXPLAIN_PATIENT_SUMMARY} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
            </>
        )
    }

    const getTextToCopy = () => {
        let anchorDate = new Date();
        if (latestFileVersions && latestFileVersions[EXPLAIN_PATIENT_SUMMARY]) {
            anchorDate = new Date(latestFileVersions[EXPLAIN_PATIENT_SUMMARY].version_date);
        }
        return `Patient Summary as of ${getLocalDateStrFromDate(anchorDate)} ${getLocalTimeStrFromDate(anchorDate)}. ` + processTextForCopy(patientSummary.patient_summary + "\n", anchorDate);
    };

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingPatientSummary} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faUser} fixedWidth /></span><strong>Patient Summary</strong> {isSectionUnread(EXPLAIN_PATIENT_SUMMARY, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right d-flex align-items-center justify-content-end">
                        {
                            latestFileVersions && patientVisit && (
                                <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={"explain_patient_summary"} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                            )
                        }

                        <CopyToClipboardButton user={user} module={"ddx"} onClick={() => {navigator.clipboard.writeText(getTextToCopy())}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy Patient Summary" />
                    
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_PATIENT_SUMMARY); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"patient_summary"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        <Row>
                            <Col md={12} className='description-text'>
                                {
                                    patientSummary && patientSummary.patient_summary ? (
                                        <>
                                            <LLMDashboardMarkdownRenderer content={patientSummary.patient_summary} scanMulti={scanMulti} />
                                        </>
                                    ) : (
                                        <>No Data</>
                                    )
                                }
                            </Col>
                        </Row>
                    </>
                )}
            </Card.Body>
        </Card>
    );
});
